// src/main.jsx
import React from 'react'
import { createRoot } from 'react-dom/client'
import StudentVideoStats from './components/StudentVideoStats/StudentVideoStats'
import StudentProgressReport from './components/StudentProgressReport/StudentProgressReport'
import EducatorQuestion from './components/EducatorQuestion/EducatorQuestion'
import StudentVideoStatsPage from './pages/StudentVideoStats'
import StudentProgressReportPage from './pages/StudentProgressReport'
import EducatorQuestionPage from './pages/EducatorQuestion'
import LessonPlanPage from './pages/LessonPlan'
import BbPreviewPage from './pages/BbPreview'
import ModuleAdmin from './components/ModuleAdmin/ModuleAdmin'
import NavigationPanel from './components/Navigation/NavigationPanel'
import QuestionControl from './components/QuestionControl/QuestionControl'
import QuestionReview from './components/QuestionReview/QuestionReview'
import ScaffoldEditor from './components/ScaffoldEditor/ScaffoldEditor'
import ScaffoldEditorPage from './pages/ScaffoldEditor'
import ScaffoldApproval from './components/ScaffoldApproval/ScaffoldApproval'
import QuestionPreviewModal from './components/QuestionEditor/QuestionPreviewModal'
import GamificationReport from './components/GamificationReport/GamificationReport'
import QuestionTopicSelection from './components/QuestionTopicSelection/QuestionTopicSelection'
import { initReactCompilation } from './utils/reactCompile'
import { getTokenForRequest } from './utils/apiAuth'
import axios from 'axios'

import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap/dist/js/bootstrap.bundle.min.js'

// Bootstrap 5 loaded externally via dynamic loader in host page
// This keeps Bootstrap 3 (legacy) and Bootstrap 5 (React) separate
// No import needed - React components will use externally loaded Bootstrap 5

// Load Bootstrap 5 if available
// if (typeof window.loadBootstrap5 === 'function') {
//   window.loadBootstrap5();
// }

// Send HttpOnly Token cookie on all API requests
axios.defaults.withCredentials = true;

// Initialize React compilation system for socrates-question.js compatibility
initReactCompilation();

// Event delegation bridge for data-react-handler attribute
// This allows buttons with data-react-handler to call React functions
document.addEventListener('click', function(event) {
  const target = event.target;
  const reactHandler = target.getAttribute('data-react-handler');
  
  if (reactHandler) {
    // Check if the handler exists in window.QuestionControl
    if (window.QuestionControl && typeof window.QuestionControl[reactHandler] === 'function') {
      event.preventDefault();
      event.stopPropagation();
      
      // Create event object matching Angular's format
      const angularEvent = {
        event: event,
        target: target
      };
      
      // Call the React function
      try {
        window.QuestionControl[reactHandler](angularEvent);
      } catch (error) {
        console.error(`Error calling React handler ${reactHandler}:`, error);
        alert(`Error: ${error.message}`);
      }
    } else {
      console.warn(`React handler '${reactHandler}' not found in window.QuestionControl`);
    }
  }
}, true); // Use capture phase to catch events early

// Store React roots by container ID to handle container recreation
// When SetupStudentProgressReport creates a new container, we need to clean up the old root
const containerRoots = new Map();

// Create a global namespace for all components
window.StudentComponents = {
  // Function to render any component in a specific container
  render: function(containerId, componentName, props = {}) {
    console.log(`StudentComponents.render called: ${componentName} in ${containerId}`);
    const container = document.getElementById(containerId)
    if (container) {
      console.log(`Container found: ${containerId}`, container);
      // Check if we have a root stored for this container ID
      let root = containerRoots.get(containerId);
      
      // Check if the stored root's container is still the same element
      // If container was recreated (new element with same ID), we need a new root
      if (root && root._container === container) {
        // Reuse existing root - just re-render (this is fast, no blank screen)
      } else {
        // Clean up old root if it exists but container changed
        if (root) {
          try {
            root.unmount();
          } catch (e) {
            // Ignore unmount errors if container was already removed
          }
        }
        // Create new root for this container
        root = createRoot(container);
        root._container = container; // Store reference to container
        containerRoots.set(containerId, root);
      }
      
      // Render immediately - React 18's render is synchronous for the initial render
      // This happens so fast that any blank from .empty() is imperceptible
      switch(componentName) {
        case 'StudentVideoStats':
          root.render(<StudentVideoStats {...props} />)
          break
        case 'StudentProgressReport':
          console.log('Rendering StudentProgressReport component');
          // Use flushSync to ensure immediate synchronous render (no blank screen)
          root.render(<StudentProgressReport {...props} />)
          break
        case 'EducatorQuestion':
          root.render(<EducatorQuestion {...props} />)
          break
        case 'ModuleAdmin':
          root.render(<ModuleAdmin {...props} />)
          break
        case 'LessonPlan':
          root.render(<LessonPlanPage {...props} />)
          break
        case 'QuestionControl':
          root.render(<QuestionControl {...props} />)
          break
        case 'ScaffoldEditor':
          root.render(<ScaffoldEditor {...props} />)
          break
        case 'QuestionReview':
          root.render(<QuestionReview {...props} />)
          break
        case 'GamificationReport':
          root.render(<GamificationReport {...props} />)
          break
        case 'QuestionTopicSelection':
          root.render(<QuestionTopicSelection {...props} />)
          break
        case 'ScaffoldApproval':
          root.render(<ScaffoldApproval {...props} />)
          break
        default:
          console.error(`Unknown component: ${componentName}`)
      }
    } else {
      console.error(`Container not found: ${containerId}`)
    }
  },

  // Specific function for rendering EducatorQuestion component
  renderEducatorQuestion: function(containerId, questionId, token) {
    const container = document.getElementById(containerId)
    if (container) {
      const root = createRoot(container)
      root.render(<EducatorQuestion questionId={questionId} token={token} />)
      return root
    } else {
      console.error(`Container not found: ${containerId}`)
    }
    return null
  },

  // Function to render NavigationPanel component
  renderNavigationPanel: function(containerId, props = {}) {
    const container = document.getElementById(containerId)
    if (container) {
      const root = createRoot(container)
      // Destructure and pass props explicitly to ensure correct structure
      const { userData = {}, permissions = [], role = '' } = props;
      root.render(
        <NavigationPanel 
          userData={userData}
          permissions={permissions}
          role={role}
        />
      )
      return root
    }
    return null
  },

  // Function to render ModuleAdmin component
  renderModuleAdmin: function(containerId, props = {}) {
    const container = document.getElementById(containerId);
    
    if (container) {
      const root = createRoot(container);
      
      // Ensure fullPageMode is properly set
      const componentProps = {
        moduleSemesterId: props.moduleSemesterId || null,
        isCoordinator: props.isCoordinator || false,
        autoShowModal: props.autoShowModal || null,
        fullPageMode: props.fullPageMode !== false,
        token: props.token || null,
        userUtilities: props.userUtilities || null,
        moduleSemesters: props.moduleSemesters || []
      };
      root.render(<ModuleAdmin {...componentProps} />);
      return root;
    }
    
    console.error(`Container not found: ${containerId}`);
    return null;
  },

  // Function to render LessonPlan component
  renderLessonPlan: function(containerId, props = {}) {
    const container = document.getElementById(containerId);
    
    if (container) {
      const root = createRoot(container);
      
      // Ensure props are properly structured for LessonPlan
      // moduleId from socrates-ui.js should be passed as moduleSemesterId
      const componentProps = {
        moduleId: props.moduleId || null,
        moduleSemesterId: props.moduleSemesterId || null,
        isCoordinator: props.isCoordinator || false,
        userUtilities: props.userUtilities || null,
        ...props
      };
      root.render(<LessonPlanPage {...componentProps} />);
      return root;
    }
    
    console.error(`Container not found: ${containerId}`);
    return null;
  },

  // Function to render QuestionControl component
  renderQuestionControl: function(containerId, props = {}) {
    const container = document.getElementById(containerId);
    
    if (container) {
      const root = createRoot(container);
      
      const componentProps = {
        questionInstance: props.questionInstance || null,
        ...props
      };
      root.render(<QuestionControl {...componentProps} />);
      return root;
    }
    
    console.error(`Container not found: ${containerId}`);
    return null;
  },

  // Function to render ScaffoldApproval component
  renderScaffoldApproval: function(containerId, token) {
    const container = document.getElementById(containerId);
    
    if (container) {
      const root = createRoot(container);
      root.render(<ScaffoldApproval token={token} />);
      return root;
    }
    
    console.error(`Container not found: ${containerId}`);
    return null;
  }
}

// Keep the original jQuery SetupScaffolds function for scaffold listing
// Only replace addScaffolding/editScaffolding functions to use React ScaffoldEditor
// This allows incremental migration - keep working jQuery list, use React for editor
if (typeof window !== 'undefined') {

  // Only replace addScaffolding/editScaffolding functions to use React ScaffoldEditor
  // This allows the scaffold list to continue using the existing jQuery code
  // The React ScaffoldEditor will be used for creating/editing scaffolds
  let scaffoldEditorRoot = null;
  let scaffoldEditorContainer = null;
  
  const closeScaffoldEditor = () => {
    if (scaffoldEditorRoot && scaffoldEditorContainer) {
      scaffoldEditorRoot.render(null);
    }
  };
  
  // Store original addScaffolding if exists (from old jQuery code)
  const originalAddScaffolding = window.addScaffolding;
  
  // Replace addScaffolding to use React ScaffoldEditor
  // The old jQuery SetupScaffolds will continue to work for the list
  window.addScaffolding = function(editQuestionID) {
    
    // Create or get container for scaffold editor modal
    if (!scaffoldEditorContainer || !scaffoldEditorRoot) {
      // Remove old container if exists
      const oldContainer = document.getElementById('scaffold-editor-react-container');
      if (oldContainer) {
        oldContainer.remove();
      }
      
      scaffoldEditorContainer = document.createElement('div');
      scaffoldEditorContainer.id = 'scaffold-editor-react-container';
      document.body.appendChild(scaffoldEditorContainer);
      scaffoldEditorRoot = createRoot(scaffoldEditorContainer);
    }

    const questionID = editQuestionID || null;

    const handleSaveToQuestion = (qId, data) => {
      if (typeof window.displayScaffold !== 'function' || typeof window.autoSaveQuestion !== 'function') {
        closeScaffoldEditor();
        return;
      }
      let scaffoldID = data.editScaffoldID != null ? data.editScaffoldID : (typeof window.scaffoldIdCounter === 'function' ? window.scaffoldIdCounter() + '' : String(-Date.now()));
      if (data.editScaffoldID != null && window.$) {
        window.$('#' + qId).find(".add-mcq-chosen-scaffold[scaffoldid='" + data.editScaffoldID + "']").remove();
      }
      window.displayScaffold(qId, parseInt(scaffoldID, 10), data.desc, data.filePath, data.fileType, data.saveMode, data.screenshotPath, data.videoType);
      window.autoSaveQuestion();
      closeScaffoldEditor();
    };

    const handleLinkFromQuestion = (linkedScaffold, description) => {
      if (questionID && typeof window.displayScaffold === 'function' && typeof window.autoSaveQuestion === 'function') {
        const filePath = linkedScaffold.FileID || linkedScaffold.fileId || '';
        const fileType = linkedScaffold.TypeDesc || linkedScaffold.scaffoldType || '';
        const screenshot = linkedScaffold.Screenshot || linkedScaffold.screenshot || null;
        const videoType = linkedScaffold.VideoType || linkedScaffold.videoType || 'Q';
        window.displayScaffold(questionID, parseInt(linkedScaffold.ID, 10), description, filePath, fileType, 'Link', screenshot, videoType);
        window.autoSaveQuestion();
      }
      closeScaffoldEditor();
    };
    
    const props = {
      show: true,
      editQuestionID: questionID,
      onHide: closeScaffoldEditor,
      onSave: (result, mode) => {
        closeScaffoldEditor();
        if (window.SetupScaffolds) {
          setTimeout(() => window.SetupScaffolds(), 500);
        }
      },
      onSaveToQuestion: questionID ? handleSaveToQuestion : undefined,
      onLink: questionID ? handleLinkFromQuestion : (scaffold, description) => {
        closeScaffoldEditor();
      }
    };
    
    scaffoldEditorRoot.render(<ScaffoldEditor {...props} />);
  };
  
  // Store original editScaffolding if exists
  const originalEditScaffolding = window.editScaffolding;
  
  // Replace editScaffolding to use React ScaffoldEditor
  window.editScaffolding = function(scaffold, editQuestionID, mode) {
    
    if (!scaffoldEditorContainer || !scaffoldEditorRoot) {
      const oldContainer = document.getElementById('scaffold-editor-react-container');
      if (oldContainer) oldContainer.remove();
      scaffoldEditorContainer = document.createElement('div');
      scaffoldEditorContainer.id = 'scaffold-editor-react-container';
      document.body.appendChild(scaffoldEditorContainer);
      scaffoldEditorRoot = createRoot(scaffoldEditorContainer);
    }

    const questionID = editQuestionID || null;

    const handleSaveToQuestion = (qId, data) => {
      if (typeof window.displayScaffold !== 'function' || typeof window.autoSaveQuestion !== 'function') {
        closeScaffoldEditor();
        return;
      }
      let scaffoldID = data.editScaffoldID != null ? data.editScaffoldID : (typeof window.scaffoldIdCounter === 'function' ? window.scaffoldIdCounter() + '' : String(-Date.now()));
      if (data.editScaffoldID != null && window.$) {
        window.$('#' + qId).find(".add-mcq-chosen-scaffold[scaffoldid='" + data.editScaffoldID + "']").remove();
      }
      window.displayScaffold(qId, parseInt(scaffoldID, 10), data.desc, data.filePath, data.fileType, data.saveMode, data.screenshotPath, data.videoType);
      window.autoSaveQuestion();
      closeScaffoldEditor();
    };

    const handleLinkFromQuestion = (linkedScaffold, description) => {
      if (questionID && typeof window.displayScaffold === 'function' && typeof window.autoSaveQuestion === 'function') {
        const filePath = linkedScaffold.FileID || linkedScaffold.fileId || '';
        const fileType = linkedScaffold.TypeDesc || linkedScaffold.scaffoldType || '';
        const screenshot = linkedScaffold.Screenshot || linkedScaffold.screenshot || null;
        const videoType = linkedScaffold.VideoType || linkedScaffold.videoType || 'Q';
        window.displayScaffold(questionID, parseInt(linkedScaffold.ID, 10), description, filePath, fileType, 'Link', screenshot, videoType);
        window.autoSaveQuestion();
      }
      closeScaffoldEditor();
    };
    
    const props = {
      show: true,
      scaffold: scaffold,
      editQuestionID: questionID,
      mode: mode || 'create',
      onHide: closeScaffoldEditor,
      onSave: (result, mode) => {
        closeScaffoldEditor();
        if (window.SetupScaffolds) setTimeout(() => window.SetupScaffolds(), 500);
      },
      onSaveToQuestion: questionID ? handleSaveToQuestion : undefined,
      onLink: questionID ? handleLinkFromQuestion : () => closeScaffoldEditor()
    };
    
    scaffoldEditorRoot.render(<ScaffoldEditor {...props} />);
  };
  
  
  // Question Preview Modal integration
  let questionPreviewRoot = null;
  let questionPreviewContainer = null;
  
  const closeQuestionPreview = () => {
    if (questionPreviewRoot && questionPreviewContainer) {
      questionPreviewRoot.render(null);
    }
  };
  
  // Global function to show question preview modal (called from Angular/jQuery code)
  window.showQuestionPreview = function(questionId, tokenParam) {
    
    // Validate inputs
    if (!questionId || questionId <= 0) {
      alert('Please save the question first before previewing.');
      return;
    }
    
    const token = getTokenForRequest();
    
    // Create or get container for question preview modal
    if (!questionPreviewContainer || !questionPreviewRoot) {
      // Remove old container if exists
      const oldContainer = document.getElementById('question-preview-react-container');
      if (oldContainer) {
        oldContainer.remove();
      }
      
      questionPreviewContainer = document.createElement('div');
      questionPreviewContainer.id = 'question-preview-react-container';
      document.body.appendChild(questionPreviewContainer);
      questionPreviewRoot = createRoot(questionPreviewContainer);
    }
    
    // Render React QuestionPreviewModal component
    const props = {
      show: true,
      questionId: questionId,
      token: token,
      onHide: closeQuestionPreview
    };
    
    questionPreviewRoot.render(<QuestionPreviewModal {...props} />);
  };
  
}

// Development mode - render pages
if (import.meta.env.DEV) {
  const root = document.getElementById('root')
  if (root) {
    const appRoot = createRoot(root)
    const path = window.location.pathname
    
    switch(path) {
      case '/student-progress':
        appRoot.render(<StudentProgressReportPage />)
        break
      case '/student-video-stats':
        appRoot.render(<StudentVideoStatsPage />)
        break
      case '/educator/question/preview':
      case '/educator-question':
        appRoot.render(<EducatorQuestionPage />)
        break
      case '/lesson-plan':
        appRoot.render(<LessonPlanPage />)
        break
      case '/scaffold-editor':
        appRoot.render(<ScaffoldEditorPage />)
        break
      case '/bbpreview':
        appRoot.render(<BbPreviewPage />)
        break
      default:
        appRoot.render(
          <div className="container mt-4">
            <h1>LearningANTS React Components</h1>
            <div className="list-group mt-4">
              <a href="/student-progress" className="list-group-item list-group-item-action">
                Student Progress Report
              </a>
              <a href="/student-video-stats" className="list-group-item list-group-item-action">
                Student Video Stats
              </a>
              <a href="/educator-question?questionId=8928&token=sample-token" className="list-group-item list-group-item-action">
                Educator Question Preview
              </a>
              <a href="/module-admin?moduleSemesterId=123&isCoordinator=true" className="list-group-item list-group-item-action">
                Module Admin
              </a>
              <a href="/lesson-plan?moduleId=123" className="list-group-item list-group-item-action">
                Lesson Plan
              </a>
              <a href="/scaffold-editor" className="list-group-item list-group-item-action">
                Scaffold Editor (New)
              </a>
              <a href="/bbpreview?bb_course_id=12345" className="list-group-item list-group-item-action">
                Blackboard Course Preview
              </a>
            </div>
          </div>
        )
    }
  }
} else {
  // Production mode - only bbpreview is standalone page, others are embedded
  document.addEventListener('DOMContentLoaded', () => {
    // Check if we're on bbpreview page (standalone React page)
    const path = window.location.pathname;
    const root = document.getElementById('root');
    
    if (root && (path === '/educator/bbpreview' || path === '/bbpreview')) {
      // Only bbpreview is a standalone page
      const appRoot = createRoot(root);
      appRoot.render(<BbPreviewPage />);
      return;
    }
    
    // All other React components are embedded (render into specific containers)
    const videoStatsContainer = document.getElementById('student-video-stats-container')
    if (videoStatsContainer) {
      window.StudentComponents.render('student-video-stats-container', 'StudentVideoStats')
    }

    const progressReportContainer = document.getElementById('student-progress-container')
    if (progressReportContainer) {
      window.StudentComponents.render('student-progress-container', 'StudentProgressReport')
    }

    const educatorQuestionContainer = document.getElementById('educator-question-container')
    if (educatorQuestionContainer) {
      window.StudentComponents.render('educator-question-container', 'EducatorQuestion')
    }
    
    // ModuleAdmin, LessonPlan, etc. are embedded and called via window.StudentComponents.render()
  })
}
